import { useState, useRef, useEffect } from "react";
import { Link } from "@remix-run/react";
import { AccountType, AccountTypeUtils } from "~/types/account";

interface JobsMenuProps {
  isMobile?: boolean;
  onLinkClick?: () => void;
  userRole?: string;
  userAccountType?: string;
}

export default function JobsMenu({ isMobile = false, onLinkClick, userRole, userAccountType }: JobsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      // Clear any pending close timeout
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      // Add a small delay before closing the menu
      closeTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
        closeTimeoutRef.current = null;
      }, 300); // 300ms delay gives enough time to move to submenu
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    if (isMobile) return;

    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile]);

  // Check if user can browse jobs using centralized logic
  const canBrowseJobs = userAccountType ?
    AccountTypeUtils.canBrowseJobs(userAccountType as AccountType, userRole) : false;

  // Check if user can view their posted jobs using centralized logic
  const canViewPostedJobs = userAccountType ?
    AccountTypeUtils.canViewPostedJobs(userAccountType as AccountType, userRole) : false;

  // Check if user can post jobs using centralized logic
  const canPostJobs = userAccountType ?
    AccountTypeUtils.canPostJobs(userAccountType as AccountType, userRole) : false;

  // Check if user can view job applications (pilots, ferry pilot agencies, site admins)
  const canViewApplications = 
    (userRole === "employee" && userAccountType === "pilot") || 
    userAccountType === "ferry_pilot_agency" || 
    userRole === "site_admin" || 
    userAccountType === "site_admin" ||
    // Include users with appropriate roles in a business account
    (userRole === "admin" && userAccountType === "business_employee") ||
    (userRole === "editor" && userAccountType === "business_employee");

  // Check if user can set up job alerts using centralized logic
  const canSetupJobAlerts = userAccountType ?
    AccountTypeUtils.canSetupJobAlerts(userAccountType as AccountType, userRole) : false;

  return (
    <div 
      ref={menuRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative"
    >
      <button 
        onClick={toggleMenu}
        className={`flex items-center ${isMobile ? "w-full text-left text-white" : ""} hover:text-gray-700`}
      >
        Jobs
        <svg 
          className="w-4 h-4 ml-1" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d={isOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} 
          />
        </svg>
      </button>
      {isOpen && (
        <div className={
          isMobile 
            ? "pl-4 border-l border-primary-500 ml-2 mt-2 space-y-3" 
            : "absolute top-full left-0 bg-white text-gray-800 rounded shadow-lg py-2 mt-1 w-48 z-50 border border-gray-200"
        }>
          {/* Browse Jobs link is now always visible for logged-in users */}
          <Link 
            to="/jobs" 
            className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
            onClick={onLinkClick}
          >
            Browse Jobs
          </Link>
          
          {canBrowseJobs && (
            <Link 
              to="/jobs/search" 
              className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
              onClick={onLinkClick}
            >
              Advanced Search
            </Link>
          )}

          {canViewPostedJobs && (
            <Link 
              to="/jobs/my" 
              className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
              onClick={onLinkClick}
            >
              My Jobs
            </Link>
          )}

          {canPostJobs && (
            <Link 
              to="/jobs/new" 
              className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
              onClick={onLinkClick}
            >
              Post Job
            </Link>
          )}

          {canViewApplications && (
            <Link 
              to="/jobs/applications" 
              className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
              onClick={onLinkClick}
            >
              My Applications
            </Link>
          )}

          {canSetupJobAlerts && (
            <Link 
              to="/notifications/watchers" 
              className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
              onClick={onLinkClick}
            >
              Job Alerts
            </Link>
          )}

          {/* Pilot Availability - only for pilots */}
          {(userRole === "pilot" || userAccountType === "pilot" || 
            (userRole === "employee" && userAccountType === "pilot")) && (
            <Link 
              to="/pilot/availability" 
              className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
              onClick={onLinkClick}
            >
              Availability Calendar
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
